import { Spacer, VDBText } from "@components/atoms";
import React from "react";
import { useTheme } from "styled-components/native";
import styled from "styled-components/native";
import { useTranslation } from "react-i18next";
import QuantitySelector from "@components/molecules/QuantitySelector";
import { Divider } from "@gluestack-ui/themed";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { hasZeroDecimals } from "./utils";
import currency from "currency.js";
import { useUserCurrency } from "../../../hooks/getCurrency";
import { css } from "styled-components/native";
import { getShowEditIcon } from "@scenes/bulk-request/Utils";
import { Diamond } from "@src/types/DiamondDetail";
import { isMeleeItem } from "@utils/product/productValidators";
import { useGetPdpSectionLayout } from "../../../hooks/useGetSectionLayout";
import { PdpSections } from "@utils/customization";
import { ProductCategory } from "@utils/constants";
import { useSelector } from "react-redux";
import { PriceModes } from "@components/molecules/PriceModeCard/types";

type PDPBottomTotalPriceSectionProps = {
  itemDetail: Diamond;
  itemQuantity: number;
  setQuantity: any;
  isReviewPage?: boolean;
  reviewTotalPrice?: Number;
};

const PDPBottomTotalPriceSection = ({
  itemDetail,
  itemQuantity,
  setQuantity,
  isReviewPage = false,
  reviewTotalPrice,
}: PDPBottomTotalPriceSectionProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const { roundUpPrices, getPriceMode } = useUserInfo();
  const userCurrency = useUserCurrency();
  const { t } = useTranslation();
  const quantitySelectorSectionLayout = useGetPdpSectionLayout(
    PdpSections.QUANTITY_SELECTOR,
    itemDetail?.type
  );
  const showQuantitySelector = getShowEditIcon(
    quantitySelectorSectionLayout,
    getPriceMode()
  );

  const allowBackOrder = itemDetail?.allow_backorder;

  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);

  const showQuantityBlock =
    showQuantitySelector &&
    !isReviewPage &&
    (!showQuantitySelector?.disable_backorder ||
      (itemDetail?.max_quantity && itemDetail?.max_quantity > 0) ||
      (itemDetail?.max_quantity === 0 && itemDetail?.allow_backorder));

  const showTotalPriceBlock = getPriceMode() !== PriceModes.GREEN;

  if (!showQuantityBlock && !showTotalPriceBlock) {
    return null;
  }

  return (
    <ViewContainer
      enableQuantity={showQuantityBlock}
      isReviewPage={isReviewPage}
    >
      {showQuantityBlock && (
        <>
          <QuantitySelector
            defaultValue={itemQuantity}
            min={1}
            max={
              (itemDetail?.type as ProductCategory) === ProductCategory.JEWELRY
                ? allowBackOrder === undefined
                  ? 99
                  : allowBackOrder
                  ? itemDetail?.max_quantity
                  : Number(itemDetail?.available_stock)
                : 99
            }
            onChange={(e: number) => setQuantity(e)}
            label={t("productDetail.bottomSection.quantity.text", "Quantity")}
          />
          <Spacer size={SpaceType.Double} />
          <Divider height={theme.isMobile ? 20 : 48} orientation="vertical" />
          <Spacer size={SpaceType.Double} />
        </>
      )}

      {showTotalPriceBlock && (
        <TotalPriceContainer enableQuantity={!itemDetail?.enable_quantity}>
          <VDBText color={theme["core-06"]} type={typography["Minor-R"]}>
            {isCustomApp && itemDetail?.is_finished_product === false
              ? t("productDetailPage.forSettingOnly.text")
              : t("productDetail.bottomSection.totalPrice.text")}
          </VDBText>
          <Spacer size={SpaceType.Basic} />
          <VDBText color={theme["core-05"]} type={typography["H1-B"]}>
            {!isReviewPage
              ? itemDetail?.total_sales_price
                ? currency(itemQuantity * itemDetail?.total_sales_price, {
                    symbol: userCurrency,
                    precision:
                      (roundUpPrices() && !isMeleeItem(itemDetail)) ||
                      hasZeroDecimals(
                        itemQuantity * itemDetail?.total_sales_price
                      )
                        ? 0
                        : 2,
                  }).format()
                : "-"
              : currency(Number(reviewTotalPrice), {
                  symbol: userCurrency,
                  precision:
                    roundUpPrices() && !isMeleeItem(itemDetail) ? 0 : 2,
                }).format()}
          </VDBText>
        </TotalPriceContainer>
      )}
    </ViewContainer>
  );
};

const ViewContainer = styled.View<{
  enableQuantity: boolean;
  isReviewPage?: boolean;
}>`
  flex-basis: auto;
  flex-direction: row;
  justify-content: space-evenly;
  align-items: center;
  ${({ enableQuantity, theme, isReviewPage }) =>
    !enableQuantity &&
    isReviewPage &&
    theme.isMobile &&
    css`
      flex: 1;
      justify-content: flex-end;
    `};
  ${({ enableQuantity, theme }) =>
    !enableQuantity &&
    theme.isMobile &&
    css`
      justify-content: flex-end;
    `};
  padding: ${SpaceType.Basic}px ${SpaceType.Double}px;
  ${({ theme }) =>
    theme.isMobile &&
    css`
      border-bottom-color: ${theme["core-03"]};
      border-bottom-width: 1px;
    `}
`;

const TotalPriceContainer = styled.View<{ enableQuantity: boolean }>`
  flex-direction: row;
  align-items: center;
  justify-content: ${({ enableQuantity }) =>
    enableQuantity ? "center" : "flex-end"};
`;

export default PDPBottomTotalPriceSection;
